import {
  participantFormIds,
  participantNameConditions,
  matchesClubStudentWhere,
  allParticipantPages
} from 'Src/helpers/twoPanelEditor/participantFilters';
import { getAllHouses } from 'Src/helpers/service/admin/houses';
import * as React from 'react';
import { History, Location } from 'history';
import { parse } from 'query-string';
import moment from 'moment';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { Club, ClubAcceptableUsers } from 'Src/models/club';
import { Loader } from 'Src/components/Loader/Loader';
import { TwoPanelEditor } from 'Src/components/TwoPanelEditor/TwoPanelEditor';
import { SendRequestPageShell } from 'Src/components/SendRequestPageShell/SendRequestPageShell';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { Team } from 'Src/models/team';
import { TYPE_OF_PLAYERS } from 'Src/consts/sport';
import { SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/common';
import { getAllSchoolActivities } from 'Src/helpers/service/admin/sports';
import { getAllSchoolTeamPlayers } from 'Src/helpers/service/admin/schoolTeamPlayers';
import { getNameAndAges, searchFunctionSchoolTeams } from 'Src/helpers/autocomplete/autocomplete';
import { ColumnDefinition } from 'Src/helpers/table/table';
import { FILTER_TYPE } from 'Src/consts/table';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import {
  getSelectOptionForForms,
  getSelectOptionForHouses,
  getSelectOptionForAge,
  getSelectOptionForGender,
  getSelectOptionsForClubPriority,
  getSelectOptionsForClubMessagesStatus
} from 'Src/helpers/table/select';
import { getUserGenderArrayConvertedFromEventGender } from 'Src/helpers/tournament/tournament';
import { CLUB_MESSAGE_INVITE_STATUS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/club';
import { formatAgesCompactForSchool } from 'Src/helpers/ageGroups';
import { getClub } from 'Src/helpers/service/admin/clubs';
import {
  addClubStudents,
  replaceClubStudents,
  getClubStudentsCountPost,
  getClubStudentsPost,
  removeClubStudentsBatchDelete
} from 'Src/helpers/service/admin/clubStudents';
import { getClubParticipants } from 'Src/helpers/service/admin/clubParticipants';
import './ClubParticipantsEditor.css';
import '../../TeamsAndEvents/Events/SchoolEventManager.scss';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

const ALL_COLUMNS = [
  { key: 'firstName', label: 'Name' },
  { key: 'lastName', label: 'Surname' },
  { key: 'parents', label: 'Parents' },
  { key: 'status', label: 'Invitation status' },
  { key: 'updated', label: 'Status updated' },
  { key: 'priority', label: 'Priority' }
];

const keyOf = (student: ClubAcceptableUsers | any) => `${student.userId || student.id}:${student.permissionId}`;
const statusOf = (student: any) => student.clubMessageStatus || student.messageStatus || '';
const statusLabelOf = (student: any) => {
  const status = statusOf(student);
  return CLUB_MESSAGE_INVITE_STATUS_SERVER_TO_CLIENT_MAPPING[status] || status;
};
const updatedOf = (student: any) => student.messageUpdatedAt || student.invitationStatusSetAt || student.updatedAt;
const priorityOf = (student: any) => student.messagePriority || student.priority;
const PAGE_SIZE = 20;
const PANEL_COLUMNS: ColumnDefinition[] = [
  { text: 'Name', field: 'firstName', accessor: ['firstName'], type: FILTER_TYPE.TEXT, isSort: false },
  { text: 'Surname', field: 'lastName', accessor: ['lastName'], type: FILTER_TYPE.TEXT, isSort: false },
  { text: 'Parents', field: 'parents', accessor: ['parents'], type: FILTER_TYPE.NONE, isSort: false },
  { text: 'Invitation status', field: 'status', accessor: statusLabelOf, type: FILTER_TYPE.NONE, isSort: false },
  { text: 'Status updated', field: 'updated', accessor: updatedOf, type: FILTER_TYPE.NONE, isSort: false },
  { text: 'Priority', field: 'priority', accessor: priorityOf, type: FILTER_TYPE.NONE, isSort: false }
];

export const ClubParticipantsEditor: React.FC<Props> = ({ user, history, location }) => {
  const clubId = String(parse(location.search).club || '');
  const userId = user.userId || 'anonymous';
  const storageKey = `columnVisibility__${userId}__clubParticipantsEditor`;
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const [futureEventsConfirmOpen, setFutureEventsConfirmOpen] = React.useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = React.useState(false);
  const [clubName, setClubName] = React.useState('');
  const [club, setClub] = React.useState<Club | undefined>();
  const [isTeamSport, setIsTeamSport] = React.useState(false);
  const [participantMode, setParticipantMode] = React.useState<'students' | 'team'>('students');
  const [teamLoading, setTeamLoading] = React.useState(false);
  const [selectedTeam, setSelectedTeam] = React.useState<Team | undefined>();
  const [teamError, setTeamError] = React.useState('');
  const [teamImportMode, setTeamImportMode] = React.useState<'add' | 'replace'>('add');
  const [replacementRequested, setReplacementRequested] = React.useState(false);
  const [teamRosterKeys, setTeamRosterKeys] = React.useState<string[]>([]);
  const teamImportBase = React.useRef<Set<string>>(new Set());
  const [houses, setHouses] = React.useState<any[]>([]);
  const [selectedForms, setSelectedForms] = React.useState<string[]>([]);
  const [selectedHouses, setSelectedHouses] = React.useState<string[]>([]);
  const eligibleRequestId = React.useRef(0);
  const countCache = React.useRef(new Map<string, any>());
  const countRequests = React.useRef(new Map<string, Promise<any>>());
  const [forms, setForms] = React.useState<any[]>([]);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [selectedAges, setSelectedAges] = React.useState<string[]>([]);
  const [selectedGenders, setSelectedGenders] = React.useState<string[]>([]);
  const [initialParticipants, setInitialParticipants] = React.useState<Set<string>>(new Set());
  const [participants, setParticipants] = React.useState<Set<string>>(new Set());
  const participantKeysRef = React.useRef(participants);
  participantKeysRef.current = participants;
  const [students, setStudents] = React.useState<ClubAcceptableUsers[]>([]);
  const [eligiblePage, setEligiblePage] = React.useState(1);
  const [eligibleTotal, setEligibleTotal] = React.useState(0);
  const [pageLoading, setPageLoading] = React.useState(false);
  const [selectingAll, setSelectingAll] = React.useState(false);
  const studentCache = React.useRef<Map<string, ClubAcceptableUsers>>(new Map());
  const initialLoadStarted = React.useRef(false);
  const [eligibleSelected, setEligibleSelected] = React.useState<Set<string>>(new Set());
  const [participantSelected, setParticipantSelected] = React.useState<Set<string>>(new Set());
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [priority, setPriority] = React.useState('');
  const [hidden, setHidden] = React.useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(storageKey) || '[]'));
    } catch (_) {
      return new Set();
    }
  });

  const loadStudents = React.useCallback(() => {
    setLoading(true);
    setLoadError('');
    countCache.current.clear();
    const request = Promise.all([
      getClub(user, clubId),
      allParticipantPages<any>(filter => getClubParticipants(user, clubId, filter), {}),
      getAllForms(user),
      getAllSchoolActivities(user, { isAllSports: true }),
      getAllHouses(user)
    ]).then(([club, participantResponse, schoolForms, activities, schoolHouses]) => {
      const participantRows: any[] = (Array.isArray(participantResponse) ? participantResponse : []).map(row => ({
        ...row,
        id: row.userId || row.id,
        isParticipant: true
      }));
      return [club, participantRows, schoolForms, activities, schoolHouses];
    });
    const timeout = new Promise((_, reject) =>
      window.setTimeout(() => reject(new Error('Participants request timed out')), 30000)
    );

    Promise.race([request, timeout])
      .then((result: any) => {
        const [club, participantResponse, schoolForms, activities, schoolHouses] = result;
        setHouses(schoolHouses || []);
        const participantRows: any[] = Array.isArray(participantResponse) ? participantResponse : [];
        const byKey = new Map<string, ClubAcceptableUsers>();
        participantRows.forEach(row => byKey.set(keyOf(row), row));
        const rows = Array.from(byKey.values());
        const current = new Set<string>(participantRows.map(keyOf));
        rows.forEach(row => studentCache.current.set(keyOf(row), row));
        setClubName(club.name);
        setClub(club);
        setIsTeamSport(
          activities.some(activity => activity.id === club.sportId && activity.players === TYPE_OF_PLAYERS.TEAM)
        );
        setForms(schoolForms || []);
        setSelectedAges(Array.isArray(club.ages) ? club.ages.map(String) : []);
        setSelectedGenders(getUserGenderArrayConvertedFromEventGender(club.gender).map(String));
        window.dispatchEvent(
          new CustomEvent('sit:topbar-breadcrumb', { detail: `Clubs / ${club.name} / Edit participants` })
        );
        setStudents(rows);
        setEligibleTotal(0);
        setEligiblePage(1);
        setParticipants(current);
        setInitialParticipants(new Set(current));
      })
      .catch(() => setLoadError('Participants could not be loaded. Please try again.'))
      .then(() => setLoading(false));
  }, [clubId]);

  React.useEffect(() => {
    if (initialLoadStarted.current) return;
    initialLoadStarted.current = true;
    loadStudents();
  }, [loadStudents]);
  React.useEffect(
    () => () => {
      window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
    },
    []
  );

  const eligibleWhere = () => {
    const where: any = { isParticipant: false, includeInvitedExceptions: true };
    const needle = search.trim();
    if (needle) where.$and = participantNameConditions(needle);
    if (status) where.messageStatus = status;
    if (priority) where.messagePriority = Number(priority);
    where.formId = { $in: participantFormIds(forms, selectedAges, selectedForms) };
    where.gender = { $in: selectedGenders };
    if (selectedHouses.length) where.houseId = { $in: selectedHouses };
    // Include staged removals, exclude staged additions, and let the server page the resulting list.
    delete where.isParticipant;
    const selectedIds = Array.from(participantKeysRef.current).map(key => key.split(':')[0]);
    if (selectedIds.length) where._id = { $nin: selectedIds };
    return where;
  };

  const queryKey = (where: any) => {
    // Invitation filters may match historical messages, so keep their counts tied to the exact roster.
    if (where.messageStatus || where.messagePriority !== undefined) return JSON.stringify(where);
    const { _id, ...filters } = where;
    return JSON.stringify(filters);
  };
  const availableCount = (snapshot: any) => {
    let total = snapshot.total;
    snapshot.participants.forEach(key => {
      const row = studentCache.current.get(key);
      if (!participantKeysRef.current.has(key) && row && matchesClubStudentWhere(row, snapshot.where)) ++total;
    });
    participantKeysRef.current.forEach(key => {
      const row = studentCache.current.get(key);
      if (!snapshot.participants.has(key) && row && matchesClubStudentWhere(row, snapshot.where)) --total;
    });
    return Math.max(0, total);
  };
  const loadEligiblePage = (page: number) => {
    const where = eligibleWhere();
    const signature = queryKey(where);
    const requestId = ++eligibleRequestId.current;
    setPageLoading(true);
    const cached = countCache.current.get(signature);
    if (cached) setEligibleTotal(availableCount(cached));
    let countRequest = countRequests.current.get(signature);
    if (!cached && !countRequest) {
      const roster = new Set(participantKeysRef.current);
      countRequest = getClubStudentsCountPost(user, clubId, where).then(
        count => {
          const snapshot = { where, total: count.count || 0, participants: roster };
          countCache.current.set(signature, snapshot);
          countRequests.current.delete(signature);
          return snapshot;
        },
        error => {
          countRequests.current.delete(signature);
          throw error;
        }
      );
      countRequests.current.set(signature, countRequest);
    }
    if (countRequest)
      countRequest
        .then(snapshot => {
          if (requestId === eligibleRequestId.current) setEligibleTotal(availableCount(snapshot));
        })
        .catch(() => {
          if (requestId === eligibleRequestId.current)
            setLoadError('Student count could not be loaded. Please try again.');
        });
    getClubStudentsPost(user, clubId, { where, limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE } as any)
      .then(eligibleRows => {
        if (requestId !== eligibleRequestId.current) return;
        const pageRows: ClubAcceptableUsers[] = Array.isArray(eligibleRows) ? eligibleRows : [];
        pageRows.forEach(row => studentCache.current.set(keyOf(row), row));
        const currentRows = Array.from(studentCache.current.values()).filter(row =>
          participantKeysRef.current.has(keyOf(row))
        );
        const byKey = new Map<string, ClubAcceptableUsers>();
        currentRows.concat(pageRows).forEach(row => byKey.set(keyOf(row), row));
        setStudents(Array.from(byKey.values()));
        setEligiblePage(page);
      })
      .catch(() => {
        if (requestId === eligibleRequestId.current)
          setLoadError('Eligible students could not be loaded. Please try again.');
      })
      .then(() => {
        if (requestId === eligibleRequestId.current) setPageLoading(false);
      });
  };

  React.useEffect(() => {
    if (loading) return;
    ++eligibleRequestId.current;
    setPageLoading(true);
    // Search is submitted explicitly by the panel; filters do not need another debounce.
    loadEligiblePage(1);
  }, [
    loading,
    search,
    status,
    priority,
    selectedAges.join(','),
    selectedGenders.join(','),
    selectedForms.join(','),
    selectedHouses.join(',')
  ]);

  const updateRoster = (next: Set<string>) => {
    const previous = participantKeysRef.current;
    participantKeysRef.current = next;
    setParticipants(next);
    setEligiblePage(1);
    const where = eligibleWhere();
    const cached = countCache.current.get(queryKey(where));
    if (cached) setEligibleTotal(availableCount(cached));
    // Show staged removals immediately while the next server page is refreshed.
    setStudents(rows => {
      const matches = Array.from(studentCache.current.values()).filter(
        row => !next.has(keyOf(row)) && matchesClubStudentWhere(row, where)
      );
      const removedRows = matches.filter(row => previous.has(keyOf(row)));
      const byKey = new Map(
        removedRows.concat(rows.filter(row => !next.has(keyOf(row)))).map(row => [keyOf(row), row])
      );
      matches.forEach(row => byKey.set(keyOf(row), row));
      return Array.from(byKey.values())
        .filter(row => matchesClubStudentWhere(row, where))
        .slice(0, PAGE_SIZE);
    });
    loadEligiblePage(1);
  };

  const visible = (key: string) => !hidden.has(key);
  const toggleColumn = (key: string) => {
    if (key === 'firstName' || key === 'lastName') return;
    const next = new Set(hidden);
    next.has(key) ? next.delete(key) : next.add(key);
    setHidden(next);
    localStorage.setItem(storageKey, JSON.stringify(Array.from(next)));
  };

  const resetColumns = () => {
    setHidden(new Set());
    localStorage.setItem(storageKey, '[]');
  };

  const filteredEligible = students.filter(student => !participants.has(keyOf(student)));
  const ageGroupsNaming = user.activeSchool && user.activeSchool.ageGroupsNaming;
  const presetAges = club && Array.isArray(club.ages) ? club.ages.map(String) : [];
  const presetGenders = club ? getUserGenderArrayConvertedFromEventGender(club.gender).map(String) : [];
  const presetFilters = { ages: presetAges, gender: presetGenders };
  const panelFilterOptions: any = {
    forms: getSelectOptionForForms(forms),
    houses: getSelectOptionForHouses(houses),
    ages: getSelectOptionForAge(forms, ageGroupsNaming),
    gender: getSelectOptionForGender(),
    status: getSelectOptionsForClubMessagesStatus(user).filter(option => option.text && option.value),
    priority: getSelectOptionsForClubPriority().filter(option => option.text && option.value !== '')
  };
  const eligible = filteredEligible;
  const current = Array.from(studentCache.current.values())
    .filter(student => participants.has(keyOf(student)))
    .sort((left: any, right: any) => {
      const surnameComparison = String(left.lastName || '').localeCompare(String(right.lastName || ''), undefined, {
        sensitivity: 'base'
      });
      return surnameComparison !== 0
        ? surnameComparison
        : String(left.firstName || '').localeCompare(String(right.firstName || ''), undefined, {
            sensitivity: 'base'
          });
    });
  const decorate = (student: any) => {
    const parents = (student.parents || []).map(parent => `${parent.firstName} ${parent.lastName}`).join(', ');
    const studentClass = propz.get(student, ['form', 'name'], '') || propz.get(student, ['form', 'ageGroup'], '');
    const studentHouse = propz.get(student, ['house', 'name'], '');
    const details = [
      studentClass ? `Class: ${studentClass}` : 'Class: Not set',
      studentHouse ? `House: ${studentHouse}` : 'House: Not set',
      visible('parents') && parents ? `Parents: ${parents}` : '',
      visible('status') && statusOf(student) ? `Status: ${statusLabelOf(student)}` : '',
      visible('updated') && updatedOf(student)
        ? `Updated: ${moment(updatedOf(student)).format('DD/MM/YYYY HH:mm')}`
        : '',
      visible('priority') && priorityOf(student) ? `Priority: ${priorityOf(student)}` : ''
    ].filter(Boolean);
    const eligibilityWarnings: string[] = [];
    const studentAge = Number(student.form && student.form.age);
    if (club && presetAges.length && (!Number.isFinite(studentAge) || !presetAges.includes(String(studentAge)))) {
      eligibilityWarnings.push(
        `Age: club is ${formatAgesCompactForSchool(
          club.ages,
          user.activeSchool
        )}, student is ${formatAgesCompactForSchool([studentAge], user.activeSchool)}`
      );
    }
    const studentGender = String(student.gender || '');
    if (club && club.gender === 'MALE_ONLY' && studentGender === 'FEMALE') {
      eligibilityWarnings.push('Gender: club is for boys, student is female');
    }
    if (club && club.gender === 'FEMALE_ONLY' && studentGender === 'MALE') {
      eligibilityWarnings.push('Gender: club is for girls, student is male');
    }
    return { ...student, transferMeta: details.join(' · '), eligibilityWarning: eligibilityWarnings.join(' · ') };
  };
  const eligibleForPanel = eligible.map(decorate);
  const currentForPanel = current.map(decorate);
  const eligibleSelectedRows = Array.from(studentCache.current.values())
    .map(decorate)
    .filter(student => eligibleSelected.has(keyOf(student)));
  const participantSelectedRows = currentForPanel.filter(student => participantSelected.has(keyOf(student)));
  const cachedStudents = Array.from(studentCache.current.values());
  const itemsAddBuffer = cachedStudents.filter(
    student => participants.has(keyOf(student)) && !initialParticipants.has(keyOf(student))
  );
  const itemsRemoveBuffer = cachedStudents.filter(
    student => !participants.has(keyOf(student)) && initialParticipants.has(keyOf(student))
  );
  const changed =
    !!selectedTeam ||
    replacementRequested ||
    Array.from(participants).some(key => !initialParticipants.has(key)) ||
    Array.from(initialParticipants).some(key => !participants.has(key));
  const participantCapacity = club && club.maxParticipants > 0 ? club.maxParticipants : undefined;
  const isOverCapacity = typeof participantCapacity === 'number' && participants.size > participantCapacity;
  const capacityOverflow = typeof participantCapacity === 'number' ? participants.size - participantCapacity : 0;

  React.useEffect(() => {
    if (!changed) return undefined;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [changed]);

  const select = (key: string, side: 'eligible' | 'participant') => {
    const source = side === 'eligible' ? eligibleSelected : participantSelected;
    const setter = side === 'eligible' ? setEligibleSelected : setParticipantSelected;
    const next = new Set(source);
    next.has(key) ? next.delete(key) : next.add(key);
    setter(next);
  };
  const move = (direction: 'add' | 'remove') => {
    if (pageLoading || selectingAll) return;
    const next = new Set(participants);
    const selected = direction === 'add' ? eligibleSelected : participantSelected;
    selected.forEach(key => (direction === 'add' ? next.add(key) : next.delete(key)));
    updateRoster(next);
    setEligibleSelected(new Set());
    setParticipantSelected(new Set());
  };
  const moveSingle = (student: ClubAcceptableUsers, direction: 'add' | 'remove') => {
    if (pageLoading || selectingAll) return;
    const next = new Set(participants);
    direction === 'add' ? next.add(keyOf(student)) : next.delete(keyOf(student));
    updateRoster(next);
    setEligibleSelected(new Set());
    setParticipantSelected(new Set());
  };
  const selectAllEligible = async () => {
    if (eligibleTotal === eligibleForPanel.length) {
      setEligibleSelected(current => new Set([...Array.from(current), ...eligibleForPanel.map(keyOf)]));
      return;
    }
    const requestId = eligibleRequestId.current;
    setSelectingAll(true);
    setPageLoading(true);
    try {
      // One request for the known result set, with one extra slot to detect concurrent additions.
      const rows = await allParticipantPages<ClubAcceptableUsers>(
        filter => getClubStudentsPost(user, clubId, filter),
        { where: eligibleWhere() },
        Math.max(PAGE_SIZE, eligibleTotal + 1)
      );
      if (requestId !== eligibleRequestId.current) return;
      rows.forEach(row => studentCache.current.set(keyOf(row), row));
      setEligibleSelected(current => new Set([...Array.from(current), ...rows.map(keyOf)]));
    } catch (_) {
      setLoadError('Students could not be selected. Please try again.');
    } finally {
      setSelectingAll(false);
      if (requestId === eligibleRequestId.current) setPageLoading(false);
    }
  };

  const importTeam = async (team: Team) => {
    if (!club || teamLoading) return;
    setTeamLoading(true);
    setTeamError('');
    try {
      const players = await getAllSchoolTeamPlayers(user, team.id, { sportId: club.sportId });
      const rows = players
        .filter(player => !Array.isArray(player.sportIds) || player.sportIds.includes(club.sportId))
        .map(
          player => ({ ...player, ...studentCache.current.get(keyOf(player)), id: player.userId || player.id } as any)
        );
      rows.forEach(row => studentCache.current.set(keyOf(row), row));
      setStudents(current => {
        const byKey = new Map(current.map(row => [keyOf(row), row]));
        rows.forEach(row => byKey.set(keyOf(row), row));
        return Array.from(byKey.values());
      });
      teamImportBase.current = new Set(participantKeysRef.current);
      const keys = rows.map(keyOf);
      setTeamRosterKeys(keys);
      updateRoster(new Set(teamImportMode === 'replace' ? keys : [...Array.from(teamImportBase.current), ...keys]));
      setReplacementRequested(teamImportMode === 'replace');
      setSelectedTeam(team);
      setEligibleSelected(new Set());
      setParticipantSelected(new Set());
    } catch (_) {
      setTeamError('The team roster could not be loaded. Please try again.');
    } finally {
      setTeamLoading(false);
    }
  };

  const changeTeamImportMode = (mode: 'add' | 'replace') => {
    setTeamImportMode(mode);
    if (!selectedTeam) return;
    const previous = new Set(
      teamImportMode === 'replace' ? teamRosterKeys : [...Array.from(teamImportBase.current), ...teamRosterKeys]
    );
    const next = new Set(
      mode === 'replace' ? teamRosterKeys : [...Array.from(teamImportBase.current), ...teamRosterKeys]
    );
    participants.forEach(key => {
      if (!previous.has(key)) next.add(key);
    });
    previous.forEach(key => {
      if (!participants.has(key)) next.delete(key);
    });
    updateRoster(next);
    setReplacementRequested(mode === 'replace');
    setEligibleSelected(new Set());
    setParticipantSelected(new Set());
  };

  const goBack = () => {
    const returnPath = propz.get(location, ['state', 'returnPath'], '/clubs');
    const returnSearch = propz.get(location, ['state', 'search'], '');
    history.push({
      pathname: returnPath,
      search: returnSearch,
      state: { clubId, currentTabIndex: 1 }
    });
  };

  const requestExit = () => {
    if (changed) {
      setExitConfirmOpen(true);
      return;
    }
    goBack();
  };

  const save = async () => {
    if (!changed || isOverCapacity || teamLoading) return;
    setSaving(true);
    setSaveError('');
    const cachedStudents = Array.from(studentCache.current.values());
    const added = cachedStudents.filter(
      student => participants.has(keyOf(student)) && !initialParticipants.has(keyOf(student))
    );
    const removed = cachedStudents.filter(
      student => !participants.has(keyOf(student)) && initialParticipants.has(keyOf(student))
    );
    const sourceImport = selectedTeam
      ? {
          sourceTeamId: selectedTeam.id,
          sourceTeamPlayers: cachedStudents
            .filter(student => participants.has(keyOf(student)) && teamRosterKeys.includes(keyOf(student)))
            .map((s: any) => ({ userId: s.userId || s.id, permissionId: s.permissionId }))
        }
      : undefined;
    try {
      if (replacementRequested) {
        await replaceClubStudents(
          user,
          clubId,
          cachedStudents
            .filter(student => participants.has(keyOf(student)))
            .map((s: any) => ({ userId: s.userId || s.id, permissionId: s.permissionId })),
          sourceImport
        );
        goBack();
        return;
      }
      if (added.length || selectedTeam) {
        await addClubStudents(user, clubId, {
          participants: added.map((s: any) => ({ userId: s.userId || s.id, permissionId: s.permissionId })),
          ...sourceImport
        });
      }
      if (removed.length) {
        await removeClubStudentsBatchDelete(user, clubId, {
          participants: removed.map((s: any) => ({ userId: s.userId || s.id, permissionId: s.permissionId }))
        });
      }
      goBack();
    } catch (error) {
      setSaveError(
        propz.get(error, ['response', 'data', 'details', 'text'], '') ||
          propz.get(error, ['response', 'data', 'message'], '') ||
          'The participant changes could not be saved. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const requestSave = () => {
    if (!changed || isOverCapacity || saving || teamLoading) return;
    if (
      club &&
      club.status === 'ACTIVE' &&
      (replacementRequested || itemsAddBuffer.length > 0 || itemsRemoveBuffer.length > 0)
    ) {
      setFutureEventsConfirmOpen(true);
      return;
    }
    void save();
  };

  if (loading) return <Loader />;
  if (loadError) {
    return (
      <SendRequestPageShell
        title="Club participants"
        subtitle={clubName || 'Participant management'}
        onBack={goBack}
        backLabel="Back to clubs"
        className="SendRequestPageShell--clubTransfer"
      >
        <div className="clubParticipantsEditor clubParticipantsEditor--embedded clubParticipantsEditor__loadError">
          <span className="clubParticipantsEditor__errorIcon" aria-hidden="true">
            !
          </span>
          <strong>Unable to load participants</strong>
          <span>{loadError}</span>
          <div>
            <button onClick={goBack}>Back to clubs</button>
            <button className="clubParticipantsEditor__save" onClick={loadStudents}>
              Retry
            </button>
          </div>
        </div>
      </SendRequestPageShell>
    );
  }
  const isTeamMode = isTeamSport && participantMode === 'team';
  const showRoster = !isTeamMode || Boolean(selectedTeam);
  const modeSwitch = isTeamSport && (
    <div className="bEventParticipantsMode" role="tablist" aria-label="Participant selection mode">
      <button
        type="button"
        role="tab"
        aria-selected={!isTeamMode}
        className={`bEventParticipantsMode__btn${!isTeamMode ? ' bEventParticipantsMode__btn--active' : ''}`}
        onClick={() => setParticipantMode('students')}
        disabled={teamLoading || saving}
      >
        Edit participants
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={isTeamMode}
        className={`bEventParticipantsMode__btn${isTeamMode ? ' bEventParticipantsMode__btn--active' : ''}`}
        onClick={() => setParticipantMode('team')}
        disabled={teamLoading || saving}
      >
        Choose existing team
      </button>
    </div>
  );
  const clubSummary = club
    ? [
        club.sport && club.sport.name,
        SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[club.gender],
        formatAgesCompactForSchool(club.ages, user.activeSchool)
      ]
        .filter(Boolean)
        .join(' · ')
    : clubName;
  return (
    <div className="clubParticipantsEditor clubParticipantsEditor--embedded clubParticipantsEditor--eventLayout">
      <SimpleModal
        isOpen={exitConfirmOpen}
        title="Leave participant management?"
        buttonText="Discard changes"
        buttonCancelText="Keep editing"
        onButtonClick={goBack}
        onCloseClick={() => setExitConfirmOpen(false)}
        customClass="clubTransferModal clubTransferExitModal"
        dialogStyle={{ maxWidth: 520 }}
        isBodyBold={false}
      >
        <div className="clubTransferModal__message isWarning">
          <span aria-hidden="true">!</span>
          <div>
            <strong>Participant changes have not been saved</strong>
            <p>Leaving this page will discard the students you added or removed.</p>
          </div>
        </div>
      </SimpleModal>
      <SimpleModal
        isOpen={Boolean(saveError)}
        title="Unable to save participants"
        onCloseClick={() => setSaveError('')}
        showFooter={false}
        customClass="clubTransferModal"
        dialogStyle={{ maxWidth: 480 }}
        isBodyBold={false}
      >
        <div className="clubTransferModal__message isDanger">
          <span aria-hidden="true">!</span>
          <div>
            <strong>The changes were not saved</strong>
            <p>{saveError}</p>
            <button type="button" onClick={() => setSaveError('')}>
              OK
            </button>
          </div>
        </div>
      </SimpleModal>
      <SimpleModal
        isOpen={futureEventsConfirmOpen}
        title={
          replacementRequested ? 'Replace the club and future event rosters?' : 'Update future event participants?'
        }
        buttonText={replacementRequested ? 'Replace participants' : 'Save changes'}
        buttonCancelText="Keep editing"
        onButtonClick={() => {
          setFutureEventsConfirmOpen(false);
          void save();
        }}
        onCloseClick={() => setFutureEventsConfirmOpen(false)}
        customClass="clubTransferModal"
        dialogStyle={{ maxWidth: 520 }}
        isBodyBold={false}
      >
        <div className="clubTransferModal__message isWarning">
          <span aria-hidden="true">!</span>
          <div>
            <strong>
              {replacementRequested
                ? 'Replace the club roster and all future event rosters with this participant list'
                : 'These changes will also update every future event in this club'}
            </strong>
            {replacementRequested && (
              <p>
                Future events with separately adjusted rosters will also be overwritten. Past events will keep their
                participants.
              </p>
            )}
            <p>
              {participants.size - itemsAddBuffer.length} retained · {itemsAddBuffer.length} added ·{' '}
              {itemsRemoveBuffer.length} removed · {participants.size} total
            </p>
            {itemsAddBuffer.length > 0 && (
              <p>
                {itemsAddBuffer.length} participant{itemsAddBuffer.length === 1 ? '' : 's'} will be added to all future
                events.
              </p>
            )}
            {itemsRemoveBuffer.length > 0 && (
              <p>
                {itemsRemoveBuffer.length} participant{itemsRemoveBuffer.length === 1 ? '' : 's'} will be removed from
                all future events.
              </p>
            )}
          </div>
        </div>
      </SimpleModal>

      {!isTeamMode && (
        <div className="bSchoolTeamManager__header">
          <div className="bSchoolTeamManager__summaryCard">
            <div className="bSchoolTeamManager__summaryMain">
              <div className="bSchoolTeamManager__summaryHeading">
                <div>
                  <div className="bSchoolTeamManager__summaryTitle">{clubSummary}</div>
                  <div className="bSchoolTeamManager__summaryDate">{clubName}</div>
                </div>
                {modeSwitch}
              </div>
            </div>
          </div>
        </div>
      )}
      <section className="bSchoolTeamManager__participantsCard clubTransferPage__card">
        <div className="bSchoolTeamManager__participantsHead clubParticipantsEditor__cardHead">
          {isTeamMode && (
            <span className="bSchoolTeamManager__participantsIcon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </span>
          )}
          <div className="clubParticipantsEditor__cardCopy">
            <h2 className="bSchoolTeamManager__participantsTitle">
              {isTeamMode ? 'Choose an existing team' : 'Select participants'}
            </h2>
            <div className="bSchoolTeamManager__participantsSubtitle">
              {isTeamMode
                ? 'Select a saved school team, then review and adjust its players before adding them to the club.'
                : 'Double-click a row to move it instantly, or tick several and use the centre buttons. Changes apply when you save.'}
            </div>
          </div>
          {isTeamMode && modeSwitch}
        </div>
        {currentForPanel.some(student => student.eligibilityWarning) && (
          <div className="alert alert-warning" role="alert">
            Some participants do not match the club year groups or gender.
          </div>
        )}
        {isOverCapacity && (
          <div className="clubTransferPage__capacityWarning" role="alert">
            This club has {participantCapacity} places. Remove {capacityOverflow} participant
            {capacityOverflow === 1 ? '' : 's'} before saving.
          </div>
        )}
        <div className="bSchoolTeamManager__participantsDivider" />
        {isTeamSport && club && participantMode === 'team' && (
          <div className="clubParticipantsEditor__teamPicker">
            <div className="clubParticipantsEditor__importModes" role="radiogroup" aria-label="Apply team roster">
              <label className={teamImportMode === 'add' ? 'is-selected' : ''}>
                <input
                  type="radio"
                  name="teamImportMode"
                  checked={teamImportMode === 'add'}
                  onChange={() => changeTeamImportMode('add')}
                  disabled={teamLoading || saving}
                />
                <span>
                  <strong>Add to current participants</strong>
                  <small>Keep the current roster and add team players. Existing pupils are not added twice.</small>
                </span>
              </label>
              <label className={teamImportMode === 'replace' ? 'is-selected' : ''}>
                <input
                  type="radio"
                  name="teamImportMode"
                  checked={teamImportMode === 'replace'}
                  onChange={() => changeTeamImportMode('replace')}
                  disabled={teamLoading || saving}
                />
                <span>
                  <strong>Replace current participants</strong>
                  <small>
                    Use the team roster as the new club roster. For active clubs, replace all future event rosters too.
                  </small>
                </span>
              </label>
            </div>
            <label className="clubParticipantsEditor__teamLabel">Existing team</label>
            <Autocomplete
              searchFunction={text =>
                searchFunctionSchoolTeams(user, {
                  text,
                  teamIds: [],
                  ages: [],
                  gender: club.gender,
                  sportId: club.sportId,
                  houses: []
                })
              }
              getElementTitle={getNameAndAges}
              onSelect={importTeam}
              defaultItem={selectedTeam}
              customClass="clubParticipantsEditor__teamAutocomplete"
              disabled={teamLoading || saving}
              showAllOnFocus
              isMenuPortal
              placeholder="Search teams by name"
            />
            <p className="clubParticipantsEditor__teamHint" aria-live="polite">
              {teamLoading
                ? 'Loading team roster…'
                : selectedTeam
                ? `Roster from ${selectedTeam.name} loaded. Saving also links this team to future roster updates. Review the participants below, then save to confirm.`
                : 'Teams match the club sport and gender for the current season.'}
            </p>
            {teamError && (
              <div role="alert" className="clubParticipantsEditor__teamError">
                {teamError}
              </div>
            )}
          </div>
        )}
        {selectedTeam && (
          <div className="clubParticipantsEditor__importSummary" aria-live="polite">
            <strong>{replacementRequested ? 'Replacement preview' : 'Addition preview'}</strong>
            <span>
              {participants.size - itemsAddBuffer.length} retained · {itemsAddBuffer.length} added ·{' '}
              {itemsRemoveBuffer.length} removed · {participants.size} total
            </span>
            <small>You can remove pupils or add other students below before saving.</small>
          </div>
        )}
        {showRoster && (
          <div className="bSchoolTeamManager__editorWrap clubParticipantsEditor__editorWrap">
            <TwoPanelEditor
              isSourceFirst
              itemsHeaderExtra={
                <label className="m-2">
                  <input
                    type="checkbox"
                    checked={!selectedAges.length}
                    onChange={event => {
                      setSelectedAges(event.target.checked ? [] : presetAges);
                      setSelectedForms([]);
                    }}
                  />{' '}
                  Include students outside club year groups
                </label>
              }
              items={eligibleForPanel}
              itemsTitle="Eligible students"
              itemsColumns={PANEL_COLUMNS.filter(column => !hidden.has(column.field))}
              itemsSelected={eligibleSelectedRows}
              onItemClick={index => select(keyOf(eligibleForPanel[index]), 'eligible')}
              itemsFilters={{
                forms: selectedForms,
                houses: selectedHouses,
                ages: selectedAges,
                gender: selectedGenders,
                status: status ? [status] : [],
                priority: priority ? [priority] : []
              }}
              onItemsFilterChange={() => undefined}
              isShowItemsFilter={filtersOpen}
              isItemsFiltered={Boolean(status || priority)}
              onItemsFilterClick={event => {
                event.preventDefault();
                setFiltersOpen(value => !value);
              }}
              itemsFiltersOptions={panelFilterOptions}
              itemsPresetFilters={presetFilters}
              itemsPresetSourceLabel="Club settings"
              itemsPresetFiltersLocked={false}
              onReplaceItemsFilters={(filters: any) => {
                setSelectedForms(filters.forms || []);
                setSelectedHouses(filters.houses || []);
                setSelectedAges(filters.ages || []);
                setSelectedGenders(filters.gender || []);
                setStatus((filters.status || [])[0] || '');
                setPriority((filters.priority || [])[0] || '');
              }}
              itemsAccumulator={currentForPanel}
              itemsAccumulatorTitle="Participants"
              itemsAccumulatorColumns={PANEL_COLUMNS.filter(column => !hidden.has(column.field))}
              itemsAccumulatorSelected={participantSelectedRows}
              onSelectVisibleAccumulatorItems={rows =>
                setParticipantSelected(current => new Set([...Array.from(current), ...rows.map(keyOf)]))
              }
              onItemAccumulatorClick={index => select(keyOf(currentForPanel[index]), 'participant')}
              itemsAccumulatorFilters={{}}
              onItemsAccumulatorFilterChange={() => undefined}
              isShowItemsAccumulatorFilter={false}
              isItemsAccumulatorFiltered={false}
              onItemsAccumulatorFilterClick={event => event.preventDefault()}
              itemsAccumulatorFiltersOptions={{}}
              onAddClick={() => move('add')}
              onRemoveClick={() => move('remove')}
              onAddSingleItem={student => moveSingle(student, 'add')}
              onRemoveSingleItem={student => moveSingle(student, 'remove')}
              itemsCount={eligibleTotal}
              itemsAccumulatorCount={currentForPanel.length}
              itemsAccumulatorCapacity={participantCapacity}
              itemCurrentPage={eligiblePage}
              itemAccumulatorCurrentPage={1}
              itemsSetCurrentPageParams={loadEligiblePage}
              itemsOnApplyFilterClick={() => loadEligiblePage(1)}
              itemsOnClearFilterClick={() => {
                setSelectedForms([]);
                setSelectedHouses([]);
                setSelectedAges(presetAges);
                setSelectedGenders(presetGenders);
                setStatus('');
                setPriority('');
              }}
              itemsAccumulatorOnApplyFilterClick={() => undefined}
              itemsAccumulatorOnClearFilterClick={() => undefined}
              onSelectAllItemsClick={selectAllEligible}
              onSelectAllItemsOnPageClick={() =>
                setEligibleSelected(current => new Set([...Array.from(current), ...eligibleForPanel.map(keyOf)]))
              }
              onUnselectAllItemsClick={() => setEligibleSelected(new Set())}
              isSelectAllItemsChecked={
                eligibleForPanel.length > 0 && eligibleSelectedRows.length === eligibleForPanel.length
              }
              onSelectAllItemsAccumulatorClick={() =>
                setParticipantSelected(current => new Set([...Array.from(current), ...currentForPanel.map(keyOf)]))
              }
              onSelectAllItemsAccumulatorOnPageClick={() =>
                setParticipantSelected(current => new Set([...Array.from(current), ...currentForPanel.map(keyOf)]))
              }
              onUnselectAllItemsAccumulatorClick={() => setParticipantSelected(new Set())}
              isSelectAllItemsAccumulatorChecked={
                currentForPanel.length > 0 && participantSelectedRows.length === currentForPanel.length
              }
              sortItemsDirection=""
              sortItemsColumnsName=""
              onSortItemsClick={() => undefined}
              sortItemsAccumulatorDirection=""
              sortItemsAccumulatorColumnsName=""
              onSortItemsAccumulatorClick={() => undefined}
              itemsAddBuffer={itemsAddBuffer}
              itemsRemoveBuffer={itemsRemoveBuffer}
              isItemsLoading={pageLoading || selectingAll}
              itemsLoadingMessage={selectingAll ? 'Selecting all eligible students…' : undefined}
              onItemsSearchChange={setSearch}
              itemsSearchValue={search}
              itemsModernPagination
              onSelectAllFilteredItemsClick={selectAllEligible}
              itemsAllColumns={PANEL_COLUMNS}
              itemsHiddenFields={hidden}
              onToggleItemsColumn={toggleColumn}
              isItemsColumnToggleable={field => field !== 'firstName' && field !== 'lastName'}
              onResetItemsColumns={resetColumns}
              itemsAccumulatorAllColumns={PANEL_COLUMNS}
              itemsAccumulatorHiddenFields={hidden}
              onToggleItemsAccumulatorColumn={toggleColumn}
              isItemsAccumulatorColumnToggleable={field => field !== 'firstName' && field !== 'lastName'}
              onResetItemsAccumulatorColumns={resetColumns}
            />
          </div>
        )}
      </section>
      <footer className="clubTransferPage__footer">
        <div>
          <span>
            {changed ? `${itemsAddBuffer.length} added · ${itemsRemoveBuffer.length} removed` : 'No pending changes'}
          </span>
        </div>
        <button onClick={requestExit}>Cancel</button>
        <button
          className="clubParticipantsEditor__save"
          disabled={!changed || saving || teamLoading || isOverCapacity}
          onClick={requestSave}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </footer>
    </div>
  );
};
